(() => {
  'use strict';

  const root = document.documentElement;
  const LAYER_SELECTOR = '[data-nmr-layer]';
  let activeLayerName = null;
  let activeLayerReturnFocus = null;

  const announce = (message) => {
    const region = document.getElementById('NmrLiveRegion');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 30);
  };

  function motionIsReduced() {
    const mode = root.dataset.nmrMotionMode || 'full';
    if (mode === 'reduced') return true;
    if (mode !== 'system') return false;
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  }

  const COLOR_MODE_KEY = 'nmr-color-mode';
  const FONT_SCALE_KEY = 'nmr-font-scale';
  const THEME_SUGGESTION_DATE_KEY = 'nmr-theme-suggestion-date-v1';
  const DAY_START_HOUR = 7;
  const NIGHT_START_HOUR = 19;

  function readStoredColorMode() {
    try {
      const saved = window.localStorage.getItem(COLOR_MODE_KEY);
      return saved === 'light' || saved === 'dark' ? saved : null;
    } catch (_error) {
      return null;
    }
  }

  function effectiveColorMode() {
    const mode = root.dataset.nmrColorMode || 'light';
    if (mode === 'dark' || mode === 'light') return mode;
    if (mode === 'system') {
      return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  }

  function syncColorModeControls() {
    const effective = effectiveColorMode();
    document.querySelectorAll('[data-nmr-color-mode-choice]').forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.nmrColorModeChoice === effective ? 'true' : 'false');
    });
  }

  function prioritizeActiveHeaderLogo(mode = effectiveColorMode()) {
    const active = document.querySelector(`.nmr-header__logo.nmr-mode-logo--${mode}`);
    const inactiveMode = mode === 'dark' ? 'light' : 'dark';
    const inactive = document.querySelector(`.nmr-header__logo.nmr-mode-logo--${inactiveMode}`);

    if (active) {
      active.loading = 'eager';
      active.fetchPriority = 'high';
    }
    if (inactive) {
      inactive.loading = 'lazy';
      inactive.fetchPriority = 'auto';
    }
  }

  function connectionAllowsIdleLogoWarmup() {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection?.saveData) return false;
    if (['slow-2g', '2g', '3g'].includes(connection?.effectiveType)) return false;
    if (Number(navigator.deviceMemory || 4) <= 2) return false;
    return true;
  }

  function warmInactiveHeaderLogo() {
    if (!connectionAllowsIdleLogoWarmup()) return;

    const activeMode = effectiveColorMode();
    const inactiveMode = activeMode === 'dark' ? 'light' : 'dark';
    const image = document.querySelector(`.nmr-header__logo.nmr-mode-logo--${inactiveMode}`);
    if (!image || image.complete) return;

    const idle = window.requestIdleCallback || ((callback) => window.setTimeout(callback, 1200));
    idle(() => {
      if (!image.isConnected || image.complete) return;
      image.fetchPriority = 'low';
      image.loading = 'eager';
      image.decode?.().catch(() => {});
    }, { timeout: 2500 });
  }

  function applyColorMode(mode, persist = true) {
    if (mode !== 'light' && mode !== 'dark') return;
    prioritizeActiveHeaderLogo(mode);
    root.dataset.nmrColorMode = mode;
    if (persist) {
      try { window.localStorage.setItem(COLOR_MODE_KEY, mode); } catch (_error) {}
    }
    syncColorModeControls();
    announce(document.getElementById('NmrDisplayI18n')?.dataset.colorModeChanged || '');
  }

  function restoreColorMode() {
    const saved = readStoredColorMode();
    if (saved) applyColorMode(saved, false);
    else {
      syncColorModeControls();
      prioritizeActiveHeaderLogo();
    }
    warmInactiveHeaderLogo();
  }

  function syncFontScaleControls(scale) {
    const current = Number.isFinite(scale) ? scale : Number.parseFloat(root.style.getPropertyValue('--nmr-user-font-scale') || '1');
    document.querySelectorAll('[data-nmr-font-scale]').forEach((button) => {
      const candidate = Number.parseFloat(button.dataset.nmrFontScale);
      button.setAttribute('aria-pressed', Math.abs(candidate - current) < .001 ? 'true' : 'false');
    });
  }

  function applyFontScale(value, persist = true) {
    const scale = Number.parseFloat(value);
    if (!Number.isFinite(scale) || scale < .9 || scale > 1.15) return;
    root.style.setProperty('--nmr-user-font-scale', String(scale));
    if (persist) {
      try { window.localStorage.setItem(FONT_SCALE_KEY, String(scale)); } catch (_error) {}
    }
    syncFontScaleControls(scale);
  }

  function restoreFontScale() {
    let restored = false;
    try {
      const saved = window.localStorage.getItem(FONT_SCALE_KEY);
      if (saved) {
        applyFontScale(saved, false);
        restored = true;
      }
    } catch (_error) {}
    if (!restored) syncFontScaleControls(1);
  }

  function localDateToken(date = new Date()) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function recommendedModeForLocalTime(date = new Date()) {
    const hour = date.getHours();
    return hour >= DAY_START_HOUR && hour < NIGHT_START_HOUR ? 'light' : 'dark';
  }

  function hideThemeSuggestion() {
    const prompt = document.querySelector('[data-nmr-theme-suggestion]');
    if (!prompt || prompt.hidden) return;
    prompt.classList.remove('is-visible');
    window.setTimeout(() => {
      if (!prompt.classList.contains('is-visible')) prompt.hidden = true;
    }, motionIsReduced() ? 0 : 240);
  }

  function markThemeSuggestionSeen() {
    try { window.localStorage.setItem(THEME_SUGGESTION_DATE_KEY, localDateToken()); } catch (_error) {}
  }

  function maybeShowTimeThemeSuggestion() {
    if (readStoredColorMode()) return;

    const prompt = document.querySelector('[data-nmr-theme-suggestion]');
    if (!prompt) return;

    try {
      if (window.localStorage.getItem(THEME_SUGGESTION_DATE_KEY) === localDateToken()) return;
    } catch (_error) {}

    const recommended = recommendedModeForLocalTime();
    if (effectiveColorMode() === recommended) return;

    if (document.visibilityState !== 'visible') {
      const showWhenVisible = () => {
        if (document.visibilityState !== 'visible') return;
        document.removeEventListener('visibilitychange', showWhenVisible);
        maybeShowTimeThemeSuggestion();
      };
      document.addEventListener('visibilitychange', showWhenVisible);
      return;
    }

    const isDark = recommended === 'dark';
    const title = prompt.querySelector('[data-nmr-theme-suggestion-title]');
    const body = prompt.querySelector('[data-nmr-theme-suggestion-body]');
    const accept = prompt.querySelector('[data-nmr-theme-suggestion-accept]');

    prompt.dataset.recommendedMode = recommended;
    if (title) title.textContent = isDark ? prompt.dataset.nightTitle : prompt.dataset.dayTitle;
    if (body) body.textContent = isDark ? prompt.dataset.nightBody : prompt.dataset.dayBody;
    if (accept) accept.textContent = isDark ? prompt.dataset.nightAction : prompt.dataset.dayAction;

    markThemeSuggestionSeen();
    prompt.hidden = false;
    requestAnimationFrame(() => prompt.classList.add('is-visible'));
  }

  function scheduleTimeThemeSuggestion() {
    window.setTimeout(maybeShowTimeThemeSuggestion, 15000);
  }

  function layerByName(name) {
    return document.querySelector(`${LAYER_SELECTOR}[data-nmr-layer="${name}"]`);
  }
  function triggerForLayer(name) {
    return document.querySelector(`[data-nmr-layer-trigger="${name}"]`);
  }
  function closeLayer(name = activeLayerName, { restoreFocus = true } = {}) {
    if (!name) return;
    const layer = layerByName(name);
    const trigger = triggerForLayer(name);
    if (!layer || layer.hidden) {
      if (activeLayerName === name) activeLayerName = null;
      return;
    }
    layer.classList.remove('is-open');
    layer.setAttribute('aria-hidden', 'true');
    trigger?.setAttribute('aria-expanded', 'false');
    window.setTimeout(() => {
      if (!layer.classList.contains('is-open')) layer.hidden = true;
    }, 230);
    if (restoreFocus) (activeLayerReturnFocus || trigger)?.focus?.({ preventScroll: true });
    activeLayerName = null;
    activeLayerReturnFocus = null;
  }
  function closeAllLayers(options = {}) {
    document.querySelectorAll(`${LAYER_SELECTOR}:not([hidden])`).forEach((layer) => {
      closeLayer(layer.dataset.nmrLayer, options);
    });
  }
  function openLayer(name, trigger) {
    const layer = layerByName(name);
    if (!layer) return;
    if (activeLayerName === name && !layer.hidden) {
      closeLayer(name);
      return;
    }
    closeAllLayers({ restoreFocus: false });
    activeLayerName = name;
    activeLayerReturnFocus = trigger || document.activeElement;
    layer.hidden = false;
    layer.setAttribute('aria-hidden', 'false');
    triggerForLayer(name)?.setAttribute('aria-expanded', 'true');
    if (name === 'display') {
      syncColorModeControls();
      syncFontScaleControls();
    }
    requestAnimationFrame(() => {
      layer.classList.add('is-open');
      if (name === 'search') {
        requestAnimationFrame(() => layer.querySelector('[data-nmr-search-input]')?.focus?.({ preventScroll: true }));
      }
    });
  }

  let menuReturnFocus = null;
  const menuDrawer = () => document.querySelector('[data-nmr-menu-drawer]');
  const menuTrigger = () => document.querySelector('[data-nmr-menu-open]');
  const activeMenuPanel = (drawer = menuDrawer()) => drawer?.querySelector('[data-nmr-menu-panel].is-active') || null;
  const focusableIn = (element) => element ? [...element.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])')].filter((node) => !node.hidden && node.getClientRects().length > 0) : [];
  const menuFocusable = (drawer = menuDrawer()) => {
    if (!drawer) return [];
    const topbar = drawer.querySelector('.nmr-menu-drawer__topbar');
    const activePanel = activeMenuPanel(drawer);
    const nodes = [...focusableIn(topbar), ...focusableIn(activePanel)];
    return nodes.filter((node, index) => nodes.indexOf(node) === index);
  };

  function openMenuDrawer() {
    const drawer = menuDrawer();
    const trigger = menuTrigger();
    if (!drawer || !trigger) return;
    closeAllLayers({ restoreFocus: false });
    menuReturnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : trigger;
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    root.classList.add('nmr-overlay-open');
    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      requestAnimationFrame(() => drawer.querySelector('.nmr-menu-drawer__close')?.focus?.({ preventScroll: true }));
    });
  }
  function closeMenuDrawer({ restoreFocus = true } = {}) {
    const drawer = menuDrawer();
    const trigger = menuTrigger();
    if (!drawer || drawer.hidden) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    trigger?.setAttribute('aria-expanded', 'false');
    root.classList.remove('nmr-overlay-open');
    window.setTimeout(() => {
      if (!drawer.classList.contains('is-open')) {
        drawer.hidden = true;
      }
    }, 360);
    if (restoreFocus) (menuReturnFocus || trigger)?.focus?.({ preventScroll: true });
    menuReturnFocus = null;
  }
  function initStickyHeader() {
    const header = document.querySelector('[data-nmr-header]');
    if (!header) return;

    let frame = 0;

    const apply = () => {
      frame = 0;
      const y = Math.max(0, window.scrollY || window.pageYOffset || 0);
      header.classList.toggle('nmr-header--glass', y >= 200);
    };

    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(apply);
    };

    apply();

    window.addEventListener('scroll', schedule, { passive: true });

    window.addEventListener('pageshow', () => {
      closeMenuDrawer({ restoreFocus: false });
      closeAllLayers({ restoreFocus: false });
      apply();
    }, { passive: true });

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') apply();
    });
  }

  function initContentProtection() {
    const body = document.body;
    if (!body || body.dataset.nmrProtection !== 'true' || window.Shopify?.designMode) return;
    if (body.dataset.nmrProtectContext === 'true') {
      document.addEventListener('contextmenu', (event) => {
        if (!event.target.closest('input,textarea,[contenteditable="true"]')) event.preventDefault();
      }, { capture: true });
    }
    if (body.dataset.nmrProtectDrag === 'true') {
      document.addEventListener('dragstart', (event) => {
        if (event.target.closest('img,svg,picture')) event.preventDefault();
      }, { capture: true });
    }
    if (body.dataset.nmrProtectShortcuts === 'true') {
      document.addEventListener('keydown', (event) => {
        const key = String(event.key || '').toLowerCase();
        const command = event.ctrlKey || event.metaKey;
        const devCombo = command && event.shiftKey && ['i','j','c'].includes(key);
        const sourceOrSave = command && ['u','s'].includes(key);
        if (key === 'f12' || devCombo || sourceOrSave) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, { capture: true });
    }
  }

  restoreColorMode();
  restoreFontScale();
  initStickyHeader();
  initContentProtection();
  scheduleTimeThemeSuggestion();

  document.addEventListener('click', (event) => {
    const layerTrigger = event.target.closest('[data-nmr-layer-trigger]');
    if (layerTrigger) {
      event.preventDefault();
      openLayer(layerTrigger.dataset.nmrLayerTrigger, layerTrigger);
      return;
    }
    const layerClose = event.target.closest('[data-nmr-layer-close]');
    if (layerClose) {
      event.preventDefault();
      closeLayer(layerClose.closest(LAYER_SELECTOR)?.dataset.nmrLayer);
      return;
    }
    const menuOpen = event.target.closest('[data-nmr-menu-open]');
    if (menuOpen) { event.preventDefault(); openMenuDrawer(); return; }
    const close = event.target.closest('[data-nmr-menu-close]');
    if (close) { event.preventDefault(); closeMenuDrawer(); return; }
    const colorMode = event.target.closest('[data-nmr-color-mode-choice]');
    if (colorMode) {
      applyColorMode(colorMode.dataset.nmrColorModeChoice);
      hideThemeSuggestion();
      return;
    }
    const scale = event.target.closest('[data-nmr-font-scale]');
    if (scale) {
      applyFontScale(scale.dataset.nmrFontScale);
      announce(document.getElementById('NmrI18n')?.dataset.textSizeChanged || '');
      return;
    }
    const suggestionAccept = event.target.closest('[data-nmr-theme-suggestion-accept]');
    if (suggestionAccept) {
      const prompt = suggestionAccept.closest('[data-nmr-theme-suggestion]');
      applyColorMode(prompt?.dataset.recommendedMode);
      hideThemeSuggestion();
      return;
    }
    const suggestionDismiss = event.target.closest('[data-nmr-theme-suggestion-dismiss]');
    if (suggestionDismiss) {
      hideThemeSuggestion();
      return;
    }
  });


  document.addEventListener('keydown', (event) => {
    const drawer = menuDrawer();
    if (drawer && !drawer.hidden) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenuDrawer();
        return;
      }
      if (event.key === 'Tab') {
        const focusables = menuFocusable(drawer);
        if (!focusables.length) return;
        const first = focusables[0];
        const last = focusables.at(-1);
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
      return;
    }
    if (event.key === 'Escape' && activeLayerName) {
      event.preventDefault();
      closeLayer(activeLayerName);
      return;
    }
  });
})();
